/**
 * DI-3-S61 §2.2 — Settings → History: what happened to THIS data set (the
 * analysis page's one DA), and what the pilot acknowledged. Read-only (AC-5):
 * acknowledging stays the pilot's, in intake.
 *
 *   1. Head      turbine · park · Inspected on … · created by · approved by /
 *                sent back / open
 *   2. Coverage  one row per blade side: the side's rating chip, its blind
 *                spots then possible gaps, each "— acknowledged by … on … · note"
 *                or "— not acknowledged", "still present after the re-run of …";
 *                the effective approval rule in one quiet line
 *   3. Timeline  newest LAST, one line each (time · actor · summary); a verdict
 *                and a run expand to their details
 *
 * One query (`useIntakeHistoryQuery`, intake/v1 1.44.0 `getIntakeHistory`).
 * Actors are rendered as the wire names them (a foreign actor arrives as
 * "<Tenant> user" — the server's `show_user_names` outcome).
 *
 * DI-3-P14-R2 §2.5 — the same panel is Intake History's History tab (exported
 * through `@aeriosiq/lyse`): `head={false}` (the dialog's header already says
 * turbine · park · creator) and `peopleFilter` — "Everything / People only"
 * over the timeline (People only = the events with an `actor`; what the
 * pipeline did has none). The analysis Settings modal keeps both defaults.
 */
import { useState, type ReactElement } from "react";
import {
  Box,
  Button,
  Collapse,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import type { ImageList } from "@aeriosiq/types";
import { typography } from "@aeriosiq/ui";
import { InfoIcon, TriangleAlertIcon } from "@aeriosiq/ui/icons";
import { imageKeys } from "../../queries/useImagesQuery.js";
import {
  approvalRuleLine,
  bladeUnitLabels,
  coveredLine,
  createdBy,
  detailLines,
  gapLine,
  hasDetails,
  headState,
  inspectedOn,
  instantLabel,
  isStoppedRun,
  orderedGaps,
  sideChip,
  sideTitle,
  stillPresentLine,
  type IntakeHistory,
  type IntakeHistoryCoverage,
  type IntakeHistoryEvent,
} from "./historyFormat.js";
import { errorStatus, useIntakeHistoryQuery } from "./useIntakeHistory.js";
import { HistoryChip, HistoryNotice, NoticeMuted } from "./HistoryNotice.js";

/** What the analysis page knows about its data set (the head's facts the history wire does not carry). */
export interface HistoryContext {
  dataAcquisitionId: string;
  /** `formatAsset` of the page's asset ("NX N149 (WEA 3)"). */
  turbine: string;
  /** `formatSite` of the page's site. */
  park: string;
  /** The acquisition's `flightDate` — the date-only fallback of "Inspected on". */
  flightDate?: string | null;
}

const small = {
  fontSize: typography.size["caption-lg"],
  color: "text.secondary",
} as const;

function SubHeading({ children }: { children: string }): ReactElement {
  return (
    <Typography
      component="h3"
      variant="subtitle2"
      sx={{
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color: "text.secondary",
        fontWeight: 600,
        mt: 2,
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

function Head({
  ctx,
  history,
  captures,
}: {
  ctx: HistoryContext;
  history: IntakeHistory;
  captures: readonly (string | null | undefined)[] | null;
}): ReactElement {
  const now = new Date();
  const windows = history.events
    .filter((e) => e.kind === "log_matched")
    .map((e) =>
      typeof e.details.windowStartUtc === "string"
        ? e.details.windowStartUtc
        : null,
    );
  const inspected = inspectedOn(
    [ctx.flightDate ?? null, ...windows],
    now,
    captures,
  );
  const creator = createdBy(history.events);
  const state = headState(history.events, now);
  const parts = [
    ctx.turbine,
    ctx.park,
    inspected ? `Inspected on ${inspected}` : null,
    creator ? `created by ${creator}` : null,
  ].filter((p): p is string => p !== null && p !== "");
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ mt: 2, flexWrap: "wrap", rowGap: 0.5 }}
      data-testid="history-head"
    >
      <Typography variant="body2" component="span">
        {parts.join(" · ")}
      </Typography>
      <Typography
        variant="body2"
        component="span"
        sx={{ color: "text.secondary" }}
        data-testid="history-head-state"
        data-state={state.kind}
      >
        · {state.text}
      </Typography>
    </Stack>
  );
}

/** DI-3-P14-R2 — the Coverage tab's words when no coverage is served. */
export const COVERAGE_NOT_ENABLED =
  "Coverage verdict was not enabled in this workflow.";

/** DI-4-6 — the words while coverage is enabled but no verdict is recorded. */
export const NO_COVERAGE_VERDICT = "No coverage verdict yet";

function Coverage({
  coverage,
  explainAbsent = false,
}: {
  coverage: IntakeHistoryCoverage | null | undefined;
  /**
   * DI-3-P14-R2 (Christian 2026-10-27) — on its own tab the absence is
   * information: say it instead of drawing nothing.
   */
  explainAbsent?: boolean;
}): ReactElement | null {
  // absent: the tenant does not hold `feature.qc_coverage` (SYNC-01) — no block
  if (coverage === undefined)
    return explainAbsent ? (
      <Box sx={{ mt: 2 }}>
        <HistoryNotice
          tone="info"
          icon={<InfoIcon fontSize="small" />}
          testId="history-coverage-not-enabled"
        >
          <b>Coverage</b> <NoticeMuted>· {COVERAGE_NOT_ENABLED}</NoticeMuted>
        </HistoryNotice>
      </Box>
    ) : null;
  // DI-3-S76 C4 — a blade group's sides by its words ("Blade A · top · SS")
  const bladeLabels = bladeUnitLabels(coverage?.sides ?? []);
  return (
    <Box data-testid="history-coverage">
      <SubHeading>Coverage</SubHeading>
      {coverage === null || coverage.sides.length === 0 ? (
        <Typography variant="body2" sx={small}>
          {NO_COVERAGE_VERDICT}
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {coverage.sides.map((s) => {
            const chip = sideChip(s);
            const covered = coveredLine(s);
            return (
              <Box
                key={`${s.bladePosition}:${s.blade?.componentId ?? ""}:${s.side}`}
                data-testid="history-coverage-side"
                data-side={`${s.bladePosition}:${s.side}`}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ fontWeight: 600, minWidth: (t) => t.spacing(10) }}
                  >
                    {sideTitle(s, bladeLabels)}
                  </Typography>
                  <HistoryChip
                    slot={chip.tone}
                    label={chip.label}
                    testId="history-coverage-chip"
                  />
                  {covered && (
                    <Typography component="span" sx={small}>
                      {covered}
                    </Typography>
                  )}
                </Stack>
                {orderedGaps(s.gaps).map((g) => {
                  const still = stillPresentLine(g, coverage.checkedAt);
                  return (
                    <Box
                      key={g.gapId}
                      sx={{ pl: 2, mt: 0.25 }}
                      data-testid="history-gap"
                      data-kind={g.kind}
                      data-acknowledged={g.acknowledgement ? "true" : "false"}
                    >
                      <Typography component="span" sx={small}>
                        {gapLine(g)}
                      </Typography>
                      {still && (
                        <Typography
                          component="span"
                          sx={small}
                          data-testid="history-gap-still-present"
                        >
                          {` · ${still}`}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Stack>
      )}
      {coverage && (
        <Typography
          variant="body2"
          sx={{ ...small, mt: 1.25 }}
          data-testid="history-approval-rule"
        >
          {approvalRuleLine(coverage.approval)}
        </Typography>
      )}
    </Box>
  );
}

function TimelineRow({ e }: { e: IntakeHistoryEvent }): ReactElement {
  const [open, setOpen] = useState(false);
  const expandable = hasDetails(e);
  const lines = expandable ? detailLines(e) : [];
  const stopped = isStoppedRun(e);
  const when = instantLabel(e.at) ?? "—";
  const who = e.actor?.name ?? null;
  return (
    <Box
      component="li"
      data-testid="history-event"
      data-kind={e.kind}
      {...(e.actor ? { "data-actor-tenant": e.actor.tenant } : {})}
      {...(stopped ? { "data-stopped": "true" } : {})}
      sx={{ listStyle: "none", py: 0.5 }}
    >
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography
          component="span"
          sx={{ ...small, flex: "none", minWidth: (t) => t.spacing(15) }}
        >
          {when}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            ...(stopped ? { color: "warning.main" } : {}),
          }}
        >
          {who && (
            <Box
              component="span"
              sx={{ fontWeight: 600 }}
              data-testid="history-event-actor"
            >
              {who}
              {" · "}
            </Box>
          )}
          {e.summary}
        </Typography>
        {expandable && lines.length > 0 && (
          <Button
            size="small"
            variant="text"
            aria-expanded={open}
            onClick={() => {
              setOpen((v) => !v);
            }}
            sx={{ flex: "none" }}
          >
            {open ? "Hide details" : "Details"}
          </Button>
        )}
      </Stack>
      {expandable && lines.length > 0 && (
        <Collapse in={open} unmountOnExit>
          <Box
            component="ul"
            sx={{ m: 0, pl: 17.5, pt: 0.25 }}
            data-testid="history-event-details"
          >
            {lines.map((l) => (
              <Typography component="li" key={l} sx={small}>
                {l}
              </Typography>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

/** DI-3-P14-R2 — the timeline filter: every event, or only those a person did. */
export type HistoryFilter = "everything" | "people";

/** DI-3-P14-R2 — "People only" keeps the events with an actor. Pure. */
export function filterEvents(
  events: readonly IntakeHistoryEvent[],
  filter: HistoryFilter,
): IntakeHistoryEvent[] {
  return filter === "people"
    ? events.filter((e) => e.actor !== null)
    : [...events];
}

function Timeline({
  history,
  peopleFilter,
}: {
  history: IntakeHistory;
  peopleFilter: boolean;
}): ReactElement {
  const [filter, setFilter] = useState<HistoryFilter>("everything");
  const events = filterEvents(
    history.events,
    peopleFilter ? filter : "everything",
  );
  return (
    <Box data-testid="history-timeline">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <SubHeading>Timeline</SubHeading>
        {peopleFilter && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filter}
            onChange={(_e, v: HistoryFilter | null) => {
              if (v) setFilter(v);
            }}
            aria-label="Which events"
            data-testid="history-filter"
            sx={{ mt: 2, mb: 0.75 }}
          >
            <ToggleButton
              value="everything"
              data-testid="history-filter-everything"
              sx={{ textTransform: "none", py: 0.25 }}
            >
              Everything
            </ToggleButton>
            <ToggleButton
              value="people"
              data-testid="history-filter-people"
              sx={{ textTransform: "none", py: 0.25 }}
            >
              People only
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>
      {history.olderOmitted && (
        <Typography variant="body2" sx={{ ...small, mb: 0.5 }}>
          Older events are left out
        </Typography>
      )}
      {events.length === 0 && (
        <Typography
          variant="body2"
          sx={small}
          data-testid="history-filter-none"
        >
          No event by a person yet
        </Typography>
      )}
      <Box component="ol" sx={{ m: 0, p: 0 }}>
        {events.map((e, i) => (
          <TimelineRow key={`${e.at}:${e.kind}:${String(i)}`} e={e} />
        ))}
      </Box>
    </Box>
  );
}

/** DI-3-P14-R2 — which part the host shows: both (the analysis app's Settings → History), or one per tab. */
export type HistoryShow = "all" | "timeline" | "coverage";

export function HistoryPanel({
  context,
  head = true,
  peopleFilter = false,
  show = "all",
}: {
  context: HistoryContext | null | undefined;
  /** DI-3-P14-R2 — false: no head line (the host's header says it). */
  head?: boolean;
  /** DI-3-P14-R2 — the "Everything / People only" filter over the timeline. */
  peopleFilter?: boolean;
  /** DI-3-P14-R2 — Intake History's dialog splits it: History = the timeline, Coverage = the verdict. */
  show?: HistoryShow;
}): ReactElement {
  if (!context) {
    return (
      <Box sx={{ mt: 2 }}>
        <HistoryNotice
          tone="info"
          icon={<InfoIcon fontSize="small" />}
          testId="history-empty"
        >
          <b>No data set</b>{" "}
          <NoticeMuted>· open one to see its history</NoticeMuted>
        </HistoryNotice>
      </Box>
    );
  }
  return (
    <HistoryBody
      ctx={context}
      head={head}
      peopleFilter={peopleFilter}
      show={show}
    />
  );
}

function HistoryBody({
  ctx,
  head,
  peopleFilter,
  show,
}: {
  ctx: HistoryContext;
  head: boolean;
  peopleFilter: boolean;
  show: HistoryShow;
}): ReactElement {
  const query = useIntakeHistoryQuery(ctx.dataAcquisitionId);
  const qc = useQueryClient();
  // the page's image list, from the cache only (no second request): its
  // capture instants give "Inspected on" its span (DI-3-S53b round 15)
  const images = qc.getQueryData<ImageList>(
    imageKeys.list(ctx.dataAcquisitionId),
  );
  const captures = images ? images.items.map((i) => i.capturedAt) : null;

  if (query.isPending) {
    return (
      <Box sx={{ mt: 2 }}>
        <HistoryNotice
          tone="info"
          icon={<InfoIcon fontSize="small" />}
          testId="history-loading"
        >
          <b>History</b> <NoticeMuted>· loading…</NoticeMuted>
        </HistoryNotice>
      </Box>
    );
  }
  if (query.isError) {
    const status = errorStatus(query.error);
    const words =
      status === 404
        ? "· this data set has no history you can read"
        : status === 403
          ? "· you cannot read this data set's history"
          : "· could not be loaded";
    return (
      <Box sx={{ mt: 2 }}>
        <HistoryNotice
          tone="error"
          icon={<TriangleAlertIcon fontSize="small" />}
          testId="history-error"
          {...(status === 404 || status === 403
            ? {}
            : {
                actions: (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      void query.refetch();
                    }}
                  >
                    Retry
                  </Button>
                ),
              })}
        >
          <b>History</b> <NoticeMuted>{words}</NoticeMuted>
        </HistoryNotice>
      </Box>
    );
  }
  const history = query.data;
  return (
    <Box data-testid="history-panel">
      {head && <Head ctx={ctx} history={history} captures={captures} />}
      {show !== "timeline" && (
        <Coverage
          coverage={history.coverage}
          explainAbsent={show === "coverage"}
        />
      )}
      {show === "coverage" ? null : history.events.length === 0 ? (
        <Box sx={{ mt: 2 }}>
          <HistoryNotice
            tone="info"
            icon={<InfoIcon fontSize="small" />}
            testId="history-no-events"
          >
            <b>Timeline</b> <NoticeMuted>· nothing recorded yet</NoticeMuted>
          </HistoryNotice>
        </Box>
      ) : (
        <Timeline history={history} peopleFilter={peopleFilter} />
      )}
    </Box>
  );
}
